import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { inject, observer } from 'mobx-react'
import yup from 'yup'

import pick from 'lodash/pick'

import { expertPaymentApi } from '@atheneum/api'

import { Formik, Form, Field } from 'formik'
import Button from '@components/Button'
import DatePicker from '@components/Form/DatePicker'
import { Dropdown } from '@components/Form/Autocomplete'
import TextArea from '@components/Form/TextArea'
import Input from '@components/Form/Input'

class CpdForm extends Component {
  static propTypes = {
    cpd: PropTypes.object.isRequired,
    epd: PropTypes.object.isRequired,
    onSubmitSuccess: PropTypes.func.isRequired,
    onSubmitError: PropTypes.func.isRequired,
    modalStore: PropTypes.object.isRequired,
    staticData: PropTypes.object.isRequired
  }

  static defaultProps = {
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: false,
    wideContent: true
  }

  handleSubmit = async (values, actions) => {
    const { cpd, onSubmitSuccess, onSubmitError } = this.props

    try {
      await expertPaymentApi.updateCpd(cpd.id, values)
      onSubmitSuccess()
    } catch (error) {
      actions.setSubmitting(false)
      onSubmitError(error)
    }
  }

  render () {
    const { modalStore, staticData, cpd, epd } = this.props
    const { APPROVED } = staticData.CONST.EXPERT_PAYMENT_DETAIL_STATUSES
    const { BUSINESS } = staticData.CONST.PAYMENT_SERVICE_TYPES
    const {
      PAYPAL,
      DIRECT_TRANSFER
    } = staticData.CONST.PAYMENT_TRANSFER_METHODS
    const {
      NO_EPD_RECEIVED,
      COLLECTING_PAYMENT_DETAILS
    } = staticData.CONST.PAYMENT_STATUSES
    const {
      paymentStatuses,
      expertPaymentDetailStatuses,
      paymentServiceTypes,
      paymentTransferMethods,
      countries
    } = staticData.names

    return (
      <div className='expert-form cpd-form'>
        <h1 className='contract-form__title'>Consultation payment detail</h1>
        <Formik
          enableReinitialize
          initialValues={pick(cpd, [
            'paymentStatusId',
            'deliveryDate',
            'datePCF',
            'datePaymentDue',
            'paymentDate',
            'invoiceNumber',
            'cpdComment'
          ])}
          validationSchema={validationSchema}
          onSubmit={this.handleSubmit}
          render={form => (
            <Form>
              <div className='expert-form__section'>
                <div className='expert-form__section__columns'>
                  <Field
                    name='paymentStatusId'
                    label='Payment status'
                    component={Dropdown}
                    disabled={epd.expertPaymentDetailStatusId !== APPROVED}
                    options={staticData.paymentStatuses.map(ps => ps.id)}
                    itemToString={item => item && paymentStatuses[item]}
                  />
                  <Field
                    name='deliveryDate'
                    label='Service delivery date'
                    dateOnly
                    component={DatePicker}
                  />
                  <Field
                    name='datePCF'
                    label='Date PCF received'
                    component={DatePicker}
                    disabled
                  />
                </div>
                <div className='expert-form__section__columns'>
                  <Field
                    name='invoiceNumber'
                    label='Invoice number'
                    component={Input}
                  />
                  <Field
                    name='datePaymentDue'
                    label='Date payment due'
                    dateOnly
                    component={DatePicker}
                  />
                  <Field
                    name='paymentDate'
                    label='Payment date'
                    dateOnly
                    maxDate={new Date()}
                    component={DatePicker}
                    disabled={[
                      NO_EPD_RECEIVED,
                      COLLECTING_PAYMENT_DETAILS
                    ].includes(cpd.paymentStatusId)}
                  />
                </div>
                <div className='expert-form__section__columns expert-form__section__columns--single'>
                  <Field
                    name='cpdComment'
                    label='Consultation payment comment'
                    component={TextArea}
                  />
                </div>
              </div>
              <h2>Expert payment details</h2>
              <div className='expert-form__section'>
                <div className='expert-form__section__columns'>
                  <ReadOnlyInput
                    label='Status'
                    value={
                      expertPaymentDetailStatuses[
                        epd.expertPaymentDetailStatusId
                      ]
                    }
                  />
                  <ReadOnlyInput
                    label='Payment service type'
                    value={paymentServiceTypes[cpd.paymentServiceTypeId]}
                  />
                  <ReadOnlyInput
                    label='Invoice needed'
                    value={cpd.invoiceNeeded ? 'Yes' : 'No'}
                  />
                </div>
                {cpd.invoiceNeeded && (
                  <div className='expert-form__section__columns'>
                    <ReadOnlyInput
                      label='VAT class'
                      value={cpd.vatClass + '%'}
                    />
                  </div>
                )}
                {cpd.paymentServiceTypeId === BUSINESS && (
                  <div className='expert-form__section__columns'>
                    <ReadOnlyInput
                      label='Company name'
                      value={cpd.companyName}
                    />
                    <ReadOnlyInput label='VAT number' value={cpd.vatNumber} />
                  </div>
                )}
                <div className='expert-form__section__columns'>
                  <ReadOnlyInput
                    label='Payment transfer method'
                    value={paymentTransferMethods[cpd.paymentTransferMethodId]}
                  />
                  {cpd.paymentTransferMethodId === PAYPAL && (
                    <ReadOnlyInput
                      label='Paypal email address'
                      value={cpd.payPalEmail}
                    />
                  )}
                  {cpd.paymentTransferMethodId === DIRECT_TRANSFER && (
                    <Fragment>
                      <ReadOnlyInput
                        label='Bank country'
                        value={countries[cpd.bankAddress.countryId]}
                      />
                      <ReadOnlyInput
                        label='Account owner'
                        value={cpd.accountOwner}
                      />
                      <ReadOnlyInput
                        label='Account number / IBAN'
                        value={cpd.accountNumber}
                      />
                      <ReadOnlyInput
                        label='BLZ / SWIFT + BIC'
                        value={cpd.bankCode}
                      />
                      <ReadOnlyInput label='Bank name' value={cpd.bankName} />
                      <ReadOnlyInput
                        label='Bank street address'
                        value={cpd.bankAddress.address1}
                      />
                      <ReadOnlyInput
                        label='Bank city'
                        value={cpd.bankAddress.city}
                      />
                      <ReadOnlyInput
                        label='Bank state'
                        value={cpd.bankAddress.state}
                      />
                      <ReadOnlyInput
                        label='Bank zip code'
                        value={cpd.bankAddress.postalCode}
                      />
                    </Fragment>
                  )}
                </div>
                <div className='expert-form__section__columns expert-form__section__columns--single'>
                  <ReadOnlyTextArea label='Comments' value={cpd.comment} />
                </div>
              </div>
              <div className='expert-form__section expert-form__section--buttons'>
                <Button
                  primary
                  submit
                  onClick={this.onSubmit}
                  disabled={form.isSubmitting}
                >
                  Save
                </Button>
                <Button onClick={modalStore.close} disabled={form.isSubmitting}>
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        />
      </div>
    )
  }
}

function ReadOnlyInput ({ label, value }) {
  return (
    <div className='expert-form__input-group'>
      <label className='expert-form__label'>{label}</label>
      <div className='expert-form__input-wrapper'>
        <input
          className='expert-form__input'
          type='text'
          value={value}
          readOnly
          disabled
        />
      </div>
    </div>
  )
}

function ReadOnlyTextArea ({ label, value }) {
  return (
    <div className='expert-form__input-group'>
      <label className='expert-form__label'>{label}</label>
      <div className='expert-form__input-wrapper'>
        <textarea
          className='expert-form__input'
          type='text'
          readOnly
          disabled
          rows={5}
        >
          {value}
        </textarea>
      </div>
    </div>
  )
}

const readOnlyPropTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string
}

ReadOnlyInput.propTypes = readOnlyPropTypes

ReadOnlyTextArea.propTypes = readOnlyPropTypes

const validationSchema = yup.object().shape({
  paymentStatusId: yup
    .number()
    .nullable()
    .required('Payment status field si required'),
  deliveryDate: yup.date().nullable(),
  datePCF: yup.date().nullable(),
  invoiceNumber: yup
    .string()
    .nullable()
    .required('Invoice number field is required'),
  datePaymentDue: yup
    .date()
    .nullable()
    .required('Date payment due field is required'),
  paymentDate: yup.date().nullable(),
  cpdComment: yup.string().nullable()
})

export default inject('modalStore', 'growlStore', 'staticData')(
  observer(CpdForm)
)
